import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { DividerModule } from "primeng/divider";
import { SelectModule } from "primeng/select";
import { InputTextModule } from "primeng/inputtext";
import { FloatLabelModule } from "primeng/floatlabel";
import { CountrySelectComponent } from "../../../../../shared/country-select/country-select.component";
import { StateSelectComponent } from "../../../../../shared/state-select/state-select.component";
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../../core/services/qpilot.service";
import { SessionService } from "../../../../../core/services/session.service";
import { PaymentIntegrationsHelper } from "../../../../../core/services/payment-gateways/payment-integrations.helper";
import { Subject, takeUntil } from "rxjs";

export interface PaymentMethodFormData {
  Type: string | null;
  GatewayCustomerId: string | null;
  GatewayPaymentId: string | null;
  GatewayPaymentType: string | null;
  Description: string | null;
  BillingFirstName: string | null;
  BillingLastName: string | null;
  BillingCountry: string | null;
  BillingStreet1: string | null;
  BillingStreet2: string | null;
  BillingCity: string | null;
  BillingState: string | null;
  BillingPostcode: string | null;
}

export interface PaymentIntegration {
  PaymentMethodType: string;
  Id?: number;
  SiteId?: number;
}

@Component({
  selector: "app-payment-method-form",
  imports: [
    CommonModule,
    FormsModule,
    DividerModule,
    SelectModule,
    InputTextModule,
    FloatLabelModule,
    CountrySelectComponent,
    StateSelectComponent,
  ],
  templateUrl: "./payment-method-form.component.html",
  styleUrl: "./payment-method-form.component.scss",
})
export class PaymentMethodFormComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() siteId: number | null = null;
  @Input() disabled: boolean = false;
  @Input() paymentMethod: PaymentMethodFormData = this.getEmptyPaymentMethod();
  @Input() accessToken?: QPilotAccessToken; // Optional token for embedded mode
  @Output() paymentMethodChange = new EventEmitter<PaymentMethodFormData>();
  @Output() validationChange = new EventEmitter<boolean>();

  paymentIntegrations: PaymentIntegration[] = [];
  paymentTypeOptions: { label: string; value: string }[] = [];
  gatewayPaymentTypeOptions: { label: string; value: string }[] = [];

  isLoading: boolean = false;

  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private paymentIntegrationsHelper: PaymentIntegrationsHelper,
  ) {}

  ngOnInit(): void {
    // Initialize country to US by default
    if (!this.paymentMethod.BillingCountry) {
      this.paymentMethod.BillingCountry = "US";
    }

    // Restore gateway payment type options if Type is already set
    // This handles the case where the form is restored after navigation
    if (this.paymentMethod.Type) {
      this.restoreGatewayPaymentTypeOptions();
    }

    // Load payment integrations from API (populates dropdown with configured gateways)
    if (this.siteId) {
      this.loadPaymentIntegrations();
    }
  }

  /**
   * Restore gateway payment type options based on current Type
   * Used when component is recreated with existing data (e.g., after navigation)
   */
  private restoreGatewayPaymentTypeOptions(): void {
    if (this.paymentMethod.Type === "Stripe") {
      const stripeTypes =
        this.paymentIntegrationsHelper.getStripePaymentTypes();
      this.gatewayPaymentTypeOptions = stripeTypes.map((type) => ({
        label: type.name,
        value: type.code,
      }));
    } else if (
      this.paymentMethod.Type === "PayPal" ||
      this.paymentMethod.Type === "PaypalV2" ||
      this.paymentMethod.Type === "PayPalV3"
    ) {
      const paypalTypes =
        this.paymentIntegrationsHelper.getPayPalPaymentTypes();
      this.gatewayPaymentTypeOptions = paypalTypes.map((type) => ({
        label: type.name,
        value: type.code,
      }));
    } else {
      this.gatewayPaymentTypeOptions = [];
    }
  }

  /**
   * Load payment integrations for the site
   * Populates dropdown with payment gateways configured for this site
   * Matches old QMC edit-payment-methods.component.html lines 46-52
   */
  loadPaymentIntegrations(): void {
    const token = this.accessToken ?? this.sessionService.accessToken;
    if (!this.siteId || !token) return;

    this.isLoading = true;
    this.qpilotService
      .getPaymentIntegrationsForSite(this.siteId, token, 1, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: { Items: PaymentIntegration[] }) => {
          this.paymentIntegrations = response.Items || [];
          // Build dropdown options from API response with display names
          this.paymentTypeOptions = this.paymentIntegrations.map((pi) => ({
            label:
              this.paymentIntegrationsHelper.getTypeDisplayName(
                pi.PaymentMethodType,
              ) || pi.PaymentMethodType,
            value: pi.PaymentMethodType,
          }));
          this.isLoading = false;
        },
        error: (error) => {
          console.error("Error loading payment integrations:", error);
          this.isLoading = false;
        },
      });
  }

  /**
   * Get display name for payment type
   */
  getPaymentTypeDisplayName(type: string): string {
    const displayNames: { [key: string]: string } = {
      Stripe: "Stripe",
      PaypalV2: "PayPal V2",
      Braintree: "Braintree",
      Authorize: "Authorize.Net",
      NMI: "NMI",
      BluePay: "BluePay",
      USAePay: "USAePay",
      Other: "Other",
    };
    return displayNames[type] || type;
  }

  /**
   * Handle payment type change
   */
  onPaymentTypeChange(): void {
    // Clear fields that depend on payment type
    this.paymentMethod.GatewayPaymentType = null;
    this.paymentMethod.GatewayCustomerId = null;
    this.paymentMethod.GatewayPaymentId = null;

    // Setup gateway-specific payment type options
    if (this.paymentMethod.Type === "Stripe") {
      // Get all 26 Stripe payment types from helper
      const stripeTypes =
        this.paymentIntegrationsHelper.getStripePaymentTypes();
      this.gatewayPaymentTypeOptions = stripeTypes.map((type) => ({
        label: type.name,
        value: type.code,
      }));
    } else if (
      this.paymentMethod.Type === "PayPal" ||
      this.paymentMethod.Type === "PaypalV2" ||
      this.paymentMethod.Type === "PayPalV3"
    ) {
      // Get all 5 PayPal payment types from helper
      const paypalTypes =
        this.paymentIntegrationsHelper.getPayPalPaymentTypes();
      this.gatewayPaymentTypeOptions = paypalTypes.map((type) => ({
        label: type.name,
        value: type.code,
      }));
    } else {
      // Other gateways don't have sub-types
      this.gatewayPaymentTypeOptions = [];
    }

    this.emitChanges();
  }

  /**
   * Handle billing country change
   */
  onBillingCountryChange(country: string): void {
    this.paymentMethod.BillingCountry = country;
    // Clear state when country changes
    if (country !== "US") {
      this.paymentMethod.BillingState = null;
    }
    this.emitChanges();
  }

  /**
   * Handle billing state change
   */
  onBillingStateChange(state: string): void {
    this.paymentMethod.BillingState = state;
    this.emitChanges();
  }

  /**
   * Emit changes
   */
  emitChanges(): void {
    this.paymentMethodChange.emit(this.paymentMethod);
    this.validateForm();
  }

  /**
   * Validate form
   */
  validateForm(): void {
    const isValid =
      !!this.paymentMethod.Type &&
      !!this.paymentMethod.BillingFirstName &&
      !!this.paymentMethod.BillingLastName &&
      !!this.paymentMethod.BillingCountry &&
      !!this.paymentMethod.BillingStreet1 &&
      !!this.paymentMethod.BillingCity &&
      !!this.paymentMethod.BillingPostcode &&
      (this.paymentMethod.BillingCountry !== "US" ||
        !!this.paymentMethod.BillingState);

    this.validationChange.emit(isValid);
  }

  /**
   * Get empty payment method object
   */
  private getEmptyPaymentMethod(): PaymentMethodFormData {
    return {
      Type: null,
      GatewayCustomerId: null,
      GatewayPaymentId: null,
      GatewayPaymentType: null,
      Description: null,
      BillingFirstName: null,
      BillingLastName: null,
      BillingCountry: "US",
      BillingStreet1: null,
      BillingStreet2: null,
      BillingCity: null,
      BillingState: null,
      BillingPostcode: null,
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
